import * as React from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import AppToast from '../../components/Toast/AppToast';
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Header from "../Includes/Header";
import { getOperationLogs, getOperationLogsUsersForFilter, deleteOldOperationLogs } from "../Apihelper/ApiHelperUser";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useTheme as useAppTheme } from "../../contexts/ThemeContext";

const ROWS_PER_PAGE = 20;

function OperationLogsPage() {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [userList, setUserList] = useState([]);
  const [filters, setFilters] = useState({
    user_id: "",
    date_from: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    date_to: dayjs().format("YYYY-MM-DD"),
    search: "",
  });
  const [openToast, setOpenToast] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [deleteOldLoading, setDeleteOldLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    const stored = localStorage.getItem("userdata");
    if (!stored) {
      navigate("/");
      return;
    }
    const { token, id } = JSON.parse(stored);
    setLoading(true);
    try {
      const body = {
        user_id: filters.user_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
        limit: ROWS_PER_PAGE,
        index: page * ROWS_PER_PAGE,
      };
      const res = await getOperationLogs(token, body);
      if (res.code === 200 || res.code === "200") {
        setLogs(Array.isArray(res.data) ? res.data : []);
        setTotalCount(res.total_count != null ? res.total_count : 0);
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error(err);
      setLogs([]);
      setTotalCount(0);
      handleSnackbarOpen("Failed to load operation logs", "error");
    } finally {
      setLoading(false);
    }
  }, [filters.user_id, filters.date_from, filters.date_to, filters.search, page, navigate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const fetchUsers = useCallback(async () => {
    const stored = localStorage.getItem("userdata");
    if (!stored) return;
    const { token } = JSON.parse(stored);
    try {
      const res = await getOperationLogsUsersForFilter(token);
      if (res.code === 200 || res.code === "200") {
        setUserList(Array.isArray(res.data) ? res.data : []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSnackbarOpen = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenToast(true);
  };
  const handleToastClose = () => setOpenToast(false);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const handleDeleteOldLogs = async () => {
    const stored = localStorage.getItem("userdata");
    if (!stored) return;
    const { token } = JSON.parse(stored);
    setDeleteOldLoading(true);
    try {
      const res = await deleteOldOperationLogs(token);
      if (res.code === 200 || res.code === "200") {
        const count = res.deleted_count != null ? res.deleted_count : 0;
        handleSnackbarOpen(
          res.message || `${count} log(s) older than 1 month deleted.`,
          "success"
        );
        fetchLogs();
        fetchUsers();
      } else {
        handleSnackbarOpen(res.message || "Failed to delete old logs", "error");
      }
    } catch (err) {
      console.error(err);
      handleSnackbarOpen("Failed to delete old logs", "error");
    } finally {
      setDeleteOldLoading(false);
    }
  };

  const formatDate = (str) => {
    if (!str) return "—";
    try {
      return dayjs(str).format("DD/MM/YYYY HH:mm");
    } catch {
      return str;
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <CssBaseline />
      <Header />
      <Box component="main" sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
        <Box sx={{ mt: "50px" }}>
          <Typography variant="h5" sx={{ color: appColors.evergreen, fontWeight: 600, mb: 3 }}>
            Operation logs
          </Typography>

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={filters.user_id}
                    label="User"
                    onChange={(e) => handleFilterChange("user_id", e.target.value)}
                  >
                    <MenuItem value="">All users</MenuItem>
                    {userList.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.name || u.user_name || `User ${u.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange("date_from", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange("date_to", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  placeholder="action, path..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "nowrap", alignItems: "center" }}>
                  <Button
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{
                      bgcolor: appColors.evergreen,
                      "&:hover": { bgcolor: appColors.pine },
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="medium"
                    startIcon={deleteOldLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteSweepIcon />}
                    onClick={handleDeleteOldLogs}
                    disabled={deleteOldLoading}
                    sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
                    title="Delete logs older than 1 month"
                  >
                    Delete old
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: appColors.evergreen }} />
              </Box>
            ) : (
              <>
                <Table size="small" aria-label="operation logs">
                  <TableHead>
                    <TableRow sx={{ bgcolor: appColors.evergreen }}>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Action</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Path</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Request</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>IP</TableCell>
                      <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          No logs found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" },
                            "&:hover": { backgroundColor: "#eff6ff" },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {row.user_name || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.user_email || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>{row.role || "—"}</TableCell>
                          <TableCell>{row.action || "—"}</TableCell>
                          <TableCell>{row.method || "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.path || "—"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.request_summary || "—"}
                          </TableCell>
                          <TableCell>{row.ip_address || "—"}</TableCell>
                          <TableCell>{formatDate(row.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handlePageChange}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                  sx={{
                    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
                    '& .MuiTablePagination-spacer': { display: 'none' },
                  }}
                />
              </>
            )}
          </TableContainer>
        </Box>
      </Box>

      <AppToast open={openToast} message={snackbarMessage} severity={snackbarSeverity} onClose={handleToastClose} />
    </Box>
  );
}

export default OperationLogsPage;
